import {GridRenderCellParams, GridValidRowModel} from "@mui/x-data-grid";
import {useCallback} from "react";
import {FaGoogle} from "react-icons/fa6";
import {PiDnaThin} from "react-icons/pi";
import {TbHexagonLetterA, TbNorthStar} from "react-icons/tb";
import {useTheCellMapNavigation} from "../../hooks/useTheCellMapNavigation";
import {Correlation, GeneCellData, Score} from "../../types/list";
import "../../css/style.css"
import {openAllianceGenomeDirect, openDepMap, openGeneCards, openGoogle} from "../ExternalLinks";
import OverlayIcon from "./ListOverlayIcon";

interface ListRenderGeneProps<T extends GridValidRowModel> {
  params: GridRenderCellParams<T, string>;
}

const ListRenderGene = <T extends Score | Correlation>({params}: ListRenderGeneProps<T>) => {
  const {row, field} = params;
  const {openOverlay} = useTheCellMapNavigation();

  const cellData = row[field as keyof T] as GeneCellData;

  const handleGeneCardClick = useCallback(() => openGeneCards(cellData.entrez), [cellData]);
  const handleDepMapClick = useCallback(() => openDepMap(cellData.symbol), [cellData]);
  const handleAllianceGenomeClick = useCallback(() => openAllianceGenomeDirect(cellData.refs?.h), [cellData]);
  const handleOverlayClick = useCallback(() => cellData.screen && openOverlay({s: cellData.screen}, true), [cellData]);
  const handleGoogleClick = useCallback(() => openGoogle(cellData.symbol), [cellData]);

  return (
    <div
      className="cell-content d-flex flex-column justify-content-center align-items-start w-100 h-100 position-relative">
      {/* Main text */}
      <span className="main-text">{cellData.display}</span>

      {/* Secondary content: muted text and icons occupy the same spot */}
      <div className="secondary-content position-relative mt-1 d-flex align-items-start justify-content-start">
        {/* Muted text */}
        {cellData.alternate && (
          <small className="alternative-name text-muted">
            {cellData.alternate}
          </small>
        )}

        {/* Icons overlay muted text */}
        <div className="icons d-flex flex-row align-items-center gap-1">
          {cellData.entrez > 0 && (  /* We have entrez ID */
            <>
              <PiDnaThin
                className="mouse-pointer icon staggered-icon staggered-0 icon-genecard"
                aria-label="GeneCards"
                title="GeneCards"
                onClick={handleGeneCardClick}
              />
              <TbNorthStar
                className="mouse-pointer icon staggered-icon staggered-1 icon-depmap"
                aria-label="DepMap"
                title="DepMap"
                onClick={handleDepMapClick}
              />
            </>
          )}
          {cellData.entrez < 0 && ( /* Custom entrez, just google the symbol. */
            <>
              <FaGoogle
                className="mouse-pointer icon staggered-icon staggered-0 icon-google"
                aria-label="Google"
                title="Google"
                onClick={handleGoogleClick}
              />
            </>
          )}
          {cellData.refs?.h && (
            <TbHexagonLetterA
              className="mouse-pointer icon staggered-icon staggered-2 icon-alliance"
              aria-label="Alliance Genome"
              title="Alliance Genome"
              onClick={handleAllianceGenomeClick}
            />
          )}
          {cellData.screen && (
            <OverlayIcon
              className="icon staggered-icon staggered-3"
              title="Overlay"
              onClick={handleOverlayClick}
            />
          )}
        </div>
      </div>
    </div>

  );
};

export default ListRenderGene;
